import { useCallback } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import { useVersioning } from '../../versioning'
import { useAnalytics, RouteParams } from '@sketch/modules-common'
import { useIsPreviewView } from '../../shares/views/PreviewView/PreviewContext'
import { useNavigateToPreviewFrame } from './useNavigateToPreviewFrame'
import type { GoToArtboardEventActionType } from './types'

// TODO: Rename this component to be consistent with FrameGroup
// https://linear.app/sketch/issue/SWEB-500/rename-common-components-between-artboards-and-frames-to

/**
 * Returns a navigateToFrame callback.
 * When rendered inside a preview context, navigation is automatically
 * redirected to the preview frame route instead of the share route.
 */
export function useNavigateToFrame() {
  const isPreviewView = useIsPreviewView()
  const navigateToShareFrame = useNavigateToShareFrame()
  const navigateToPreviewFrame = useNavigateToPreviewFrame()

  return isPreviewView ? navigateToPreviewFrame : navigateToShareFrame
}

function useNavigateToShareFrame() {
  const history = useHistory()
  const getArtboardViewUrlFromUUID = useGetArtboardViewUrlFromUUID()
  const { trackEvent } = useAnalytics()

  return useCallback(
    (uuid: string, actionType: GoToArtboardEventActionType) => {
      trackEvent('CANVAS - open artboard', { actionType })
      history.push(getArtboardViewUrlFromUUID(uuid))
    },
    [trackEvent, history, getArtboardViewUrlFromUUID]
  )
}

/**
 * Provide a function that takes an artboard UUID and builds the URL
 * to access the artboard.
 */
function useGetArtboardViewUrlFromUUID() {
  const { getPathname } = useVersioning()
  const { shareID } = useParams<RouteParams<'SHARE_PAGE_CANVAS_VIEW'>>()

  const getArtboardUrlFromUUID = useCallback(
    (frameGroupUUID: string) => {
      return getPathname({
        routeKey: 'FRAME',
        routeParams: {
          shareID: shareID,
          frameUUID: frameGroupUUID,
        },
      })
    },
    [getPathname, shareID]
  )

  return getArtboardUrlFromUUID
}
